export type TreeNode<Attributes = unknown> = {
    id: string;
    children?: TreeNode<Attributes>[];
    attributes: Attributes;
};

export type GetIds<Attributes = unknown> = (att: Attributes) => {
    id: string;
    parentId: string | undefined;
};

export type TreeNodeCreator<Attributes = unknown, Node = TreeNode<Attributes>> = (
    item: Attributes,
    id: string,
    children: Node[]
) => Node;

export function createCustomTree<Attributes, Node = TreeNode<Attributes>>(
    items: Attributes[],
    getIds: GetIds<Attributes>,
    nodeCreator: TreeNodeCreator<Attributes, Node>
) {
    const queue: Node[] = [];

    const mapNodes = new Map<string, Node>();
    const mapChildren = new Map<string, Node[]>();

    items.forEach((item) => {
        const { id, parentId } = getIds(item);

        let node = mapNodes.get(id);

        if (!node) {
            const children = mapChildren.get(id) ?? [];

            node = nodeCreator(item, id, children);
            mapChildren.set(id, children);
        }

        mapNodes.set(id, node);

        if (parentId) {
            const children = mapChildren.get(parentId) ?? [];

            children.push(node);
            mapChildren.set(parentId, children);
        } else {
            queue.push(node);
        }
    });

    return queue;
}

export function createTree<Attributes>(items: Attributes[], getIds: GetIds<Attributes>): TreeNode<Attributes>[] {
    return createCustomTree<Attributes, TreeNode<Attributes>>(items, getIds, (item, id, children) => {
        return {
            id,
            attributes: item,
            children,
        };
    });
}

export function walkTreeTopDown<Node = TreeNode<unknown>, ParentData = unknown>(
    root: Node,
    getChildren: (node: Node) => Node[],
    cb: (node: Node, parentData?: ParentData) => ParentData
) {
    function topDown(node: Node, parentData?: ParentData) {
        const data = cb(node, parentData);

        getChildren(node).forEach((child) => topDown(child, data));
    }

    topDown(root);
}

// Функция для преобразования дерева в плоский список
type TransformFunction<Attributes = unknown, U = unknown> = (
    // Атрибуты текущего узла
    attributes: Attributes,
    // Индекс текущего узла в списке дочерних узлов
    index: number,
    // Уровень вложенности текущего узла
    level: number,
    // Родители от текущего узла до корневой ноды
    parents: Attributes[]
) => U;

// Функция для преобразования дерева в плоский список
export function flattenTree<Attributes = unknown, U = unknown>(
    root: TreeNode<Attributes>,
    transform: TransformFunction<Attributes, U>
): U[] {
    const result: U[] = [];

    function walk(node: TreeNode<Attributes>, index: number, level: number, parents: Attributes[]) {
        const transformed = transform(node.attributes, index, level, parents);

        result.push(transformed);

        if (node.children) {
            for (let i = 0; i < node.children.length; i++) {
                walk(node.children[i], i, level + 1, [node.attributes, ...parents]);
            }
        }
    }

    // Начинаем обход с корневого узла
    walk(root, 0, 0, []);

    return result;
}
